import { useMemo } from "react";
import Layout from "../components/Layout";
import { useCompanies } from "../hooks/useQueries";
import SearchBar from "../components/SearchBar";
import StockCard from "../components/StockCard";

const FEATURED_TICKERS = [
  "NVDA",
  "AAPL",
  "MSFT",
  "AMZN",
  "GOOGL",
  "META",
  "TSLA",
  "JPM",
  "V",
  "WMT",
];

/**
 * TerminalPage — The research terminal start page with search & top stocks.
 */
export default function TerminalPage() {
  const { data, isLoading } = useCompanies();
  const companies = data?.companies ?? [];
  const count = data?.count ?? 0;

  const topStocks = useMemo(() => {
    const compMap = new Map(companies.map((c) => [c.ticker.toUpperCase(), c]));
    const picked: typeof companies = [];
    for (const t of FEATURED_TICKERS) {
      const c = compMap.get(t);
      if (c) picked.push(c);
    }
    if (picked.length < 10) {
      for (const c of companies) {
        if (!picked.some((p) => p.ticker === c.ticker) && !c.is_spac && (c.filings_count ?? 0) > 0) {
          picked.push(c);
          if (picked.length >= 10) break;
        }
      }
    }
    return picked.length > 0 ? picked : companies.slice(0, 10);
  }, [companies]);

  return (
    <Layout>
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12 gap-10">
        {/* Terminal Title & Search */}
        <div className="flex flex-col items-center gap-3 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
            Financial Research Terminal
          </h1>
          <p className="text-text-secondary text-xs sm:text-sm max-w-md">
            Auditable company financials, raw XBRL facts, and filings history.
          </p>
        </div>

        {/* Hero Search Bar */}
        <SearchBar size="hero" />

        {/* Top Stocks Grid */}
        <div className="w-full max-w-5xl">
          {isLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-card-dark border border-card-border p-4 h-36 animate-pulse"
                />
              ))}
            </div>
          ) : topStocks.length > 0 ? (
            <>
              <p className="text-text-secondary text-xs font-medium uppercase tracking-wider mb-4">
                Top Stocks
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {topStocks.map((c) => (
                  <StockCard key={c.ticker} exchange={c.exchange || "US"} ticker={c.ticker} name={c.name} />
                ))}
              </div>
            </>
          ) : null}
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 text-xs text-text-secondary">
          <span className="size-2 rounded-full bg-accent-green" />
          <span>{count} companies active</span>
        </div>
      </div>
    </Layout>
  );
}
